import { User } from "@prisma/client";
import { FaTelegramPlane } from "react-icons/fa";
import {
    FaInstagram,
    FaLinkedin,
    FaXTwitter,
    FaWhatsapp,
} from "react-icons/fa6";

import { slugifyUser } from "~/utils/string";
import { ChatLinks, SocialLinks } from "~/types/UserTypes";

/** Parse Saved URLS (returns only the username) */
export const parseSocialLinks = (user: User) => {
    const socialLinks = (user?.socialLinks as SocialLinks) ?? {};
    const updatedSocialLinks: Partial<SocialLinks> = {};
    const sliceLengths: Record<string, number> = {
        x: 20,
        ig: 26,
        li: 28,
        fb: 25,
    };

    for (const [key, value] of Object.entries(socialLinks)) {
        if (!value?.startsWith("http") || value.length === 0) continue;

        if (sliceLengths[key]) {
            updatedSocialLinks[key as keyof Partial<SocialLinks>] = value.slice(
                sliceLengths[key],
            );
        }
    }

    const newUser = {
        ...user,
        socialLinks: {
            ...socialLinks,
            ...updatedSocialLinks,
        },
    };

    return newUser;
};

export const getDefaultFormValues = (user?: User) => {
    if (!user) return;

    const newUser = parseSocialLinks(user);
    const [fName, ...lName] = newUser?.name ? newUser.name.split(" ") : [];

    const defValues = {
        userId: newUser.id,
        images: {
            image: newUser?.image ?? "",
            bannerImage: newUser?.bannerImage ?? "",
            organizationImage: newUser?.organizationImage ?? undefined,
        },
        firstName: fName || undefined,
        lastName: lName?.join(" ") || undefined,
        phone: newUser.phone || undefined,
        slug: slugifyUser(newUser),
        chatLinks: {
            whatsapp: (newUser?.chatLinks as ChatLinks)?.whatsapp ?? undefined,
            telegram: (newUser?.chatLinks as ChatLinks)?.telegram ?? undefined,
        },
        socialLinks: {
            x: (newUser?.socialLinks as SocialLinks)?.x ?? undefined,
            li: (newUser?.socialLinks as SocialLinks)?.li ?? undefined,
            ig: (newUser?.socialLinks as SocialLinks)?.ig ?? undefined,
        },
        email: newUser?.email ?? undefined,
        bio: newUser?.bio ?? undefined,
        organizationName: newUser?.organizationName ?? undefined,
        organizationLink: newUser?.organizationLink ?? undefined,
        useOrganizationIdentity: newUser?.useOrganizationIdentity ?? false,
    };

    return defValues;
};

export const socialLinkFields = [
    {
        icon: FaXTwitter,
        name: "socialLinks.x",
        id: "socialMediaX",
        placeholder: "Paste X / Twitter Profile Link",
        type: "x",
    },
    {
        icon: FaInstagram,
        name: "socialLinks.ig",
        id: "socialMediaInstagram",
        placeholder: "Paste Instagram Profile Link",
        type: "ig",
    },
    {
        icon: FaLinkedin,
        name: "socialLinks.li",
        id: "socialMediaLinkedIn",
        placeholder: "Paste LinkedIn Profile Link",
        type: "li",
    },
] as const;

export const chatLinks = [
    {
        icon: FaWhatsapp,
        name: "chatLinks.whatsapp",
        id: "whatsapp",
        placeholder: "+1-whatsapp-number",
        type: "w",
    },
    {
        icon: FaTelegramPlane,
        name: "chatLinks.telegram",
        id: "telegram",
        placeholder: "@telegram-profile",
        type: "tg",
    },
] as const;

export const bgBannerColors = [
    {
        id: "r1",
        value: "linear-gradient(135deg, #1E3A8A 20%, rgba(96,165,250, 0.8) 80%)",
    },
    {
        id: "r2",
        value: "linear-gradient(135deg, #065F46 20%, rgba(52,211,153, 0.8) 80%)",
    },
    {
        id: "r3",
        value: "linear-gradient(135deg, #9A3412 20%, rgba(251,146,60, 0.8) 80%)",
    },
] as const;
